// Editorial dates stay fixed across builds and change only when the prices
// are re-checked. The Contentful node keeps the route, original publish date
// and guide-hub listing; the copy lives here. Home running costs reuse the
// printer cost guide's ledger so the two guides never disagree.
const {
  electricity,
  consumables,
  ownerSources,
  centsFor,
} = require('./printer-cost-guide')

const printCostGuide = {
  path: '/guides/how-much-does-3d-printing-cost/',
  title: 'How much does 3D printing cost in 2026?',
  metaTitle: 'How Much Does 3D Printing Cost? 2026 Prices, Home vs. Service',
  description:
    'A 100 g PLA print costs about $1–$2.50 at home; professional 3D printing starts around $30. What drives a quote, price examples, and ways to pay less.',
  reviewedDate: '2026-10-27',
  reviewedDateLabel: 'October 27, 2026',
}

// Worked home example: a palm-sized PLA part on an owner-measured ~110 W
// printer. PLA prices are the cheapest and dearest 1 kg spools in the ledger.
const homeExample = {
  grams: 100,
  hours: 5,
  watts: 110,
  plaPerKg: { low: 9.74, high: 23.99 },
}

const homeExampleCosts = (() => {
  const { grams, hours, watts, plaPerKg } = homeExample
  const power = Number(centsFor(watts, hours)) / 100
  const low = (grams / 1000) * plaPerKg.low
  const high = (grams / 1000) * plaPerKg.high
  return {
    filamentLow: low.toFixed(2),
    filamentHigh: high.toFixed(2),
    powerCents: Math.round(power * 100),
    totalLow: (low + power).toFixed(2),
    totalHigh: (high + power).toFixed(2),
  }
})()

// PrintAWorld's own published prices, as recorded in .seo/truth.md. Change
// these only when the owner changes the public price.
const routes = [
  {
    route: 'Your own filament printer',
    cost: `about $${homeExampleCosts.totalLow}–$${homeExampleCosts.totalHigh} per 100 g print`,
    includes:
      'Material and electricity only. You prepare the file, print, remove supports and finish.',
    fit: 'Frequent small parts in PLA or PETG, with time to tune the machine.',
  },
  {
    route: 'Your own resin printer',
    cost: '$400 to over $2,000 to get started',
    includes:
      'Printer, resin, alcohol, gloves, a respirator and a wash-and-cure station.',
    fit: 'Small, finely detailed parts such as miniatures, if you have a ventilated space.',
  },
  {
    route: 'Online instant-quote service',
    cost: 'priced automatically from your file',
    includes:
      'Usually printing in a chosen material, plus shipping. Order minimums are common; file repair and painting usually are not included.',
    fit: 'Print-ready engineering parts where a standard finish is enough.',
  },
  {
    route: 'Full-service studio (PrintAWorld)',
    cost: 'from about $30; finished pieces $250+',
    includes:
      'File review, printing, support removal and any quoted modeling, sanding, painting or plating, then pickup in New York or US delivery.',
    fit: 'One-off or display pieces, large builds, finished surfaces or a fixed deadline.',
  },
]

const quoteDrivers = [
  {
    id: 'file',
    label: 'File review and preparation',
    detail:
      'We check walls, gaps and hidden geometry before anything prints. A client once sent a stadium model with loose joints, walls too thin to print and extra models buried inside the structure. Printed as-is, it would have wasted the material. Small fixes are part of preparation; building a model from a sketch is separate 3D modeling, which starts at $250.',
  },
  {
    id: 'material',
    label: 'Material and volume',
    detail:
      'Price follows how much material the part uses, supports included. Size alone is misleading: a hollow 3-foot piece can use less material than a solid 1-foot piece.',
  },
  {
    id: 'machine',
    label: 'Process and machine time',
    detail:
      'Filament, resin (SLA), large-format and stainless-steel metal printing run on different machines at different rates. Taller parts and finer layers take longer, and print time is only part of the turnaround.',
  },
  {
    id: 'cleanup',
    label: 'Support removal and cleanup',
    detail:
      'Resin parts are washed, cured and cleared of supports. Intricate models with supports in hard-to-reach places take hand work.',
  },
  {
    id: 'finish',
    label: 'Finishing',
    detail:
      'Sanding, priming, painting and metal plating often cost more than the print. Plating adds a metal surface; it does not make the part solid metal.',
  },
  {
    id: 'quantity',
    label: 'Quantity and minimum',
    detail:
      'Setup and file preparation are shared across every piece in an order, so the price per piece usually drops as quantity rises.',
  },
  {
    id: 'delivery',
    label: 'Deadline, packing and delivery',
    detail:
      'Rush work needs review and depends on capacity. Fragile and large pieces need custom packing, and shipping is quoted to your ZIP code.',
  },
]

// Examples built from published prices, not individual customer jobs.
const examples = [
  {
    title: 'A replacement part or prototype from your own file',
    price: 'from about $30',
    detail:
      'Simple parts from a print-ready STL, OBJ, STEP or 3DM file sit at the low end. The quote confirms material, quantity and any minimum.',
    link: { to: '/services/3d-printing/', label: '3D printing service' },
  },
  {
    title: 'A custom 3D printed trophy, 8–12 inches tall',
    price: '$250–$750 per piece',
    detail:
      'Painted resin trophies usually land here. Complex multi-color finishes, metal plating, large sizes or gold finishes run $750–$2,000+, and trophies 16 inches or taller start at $1,500. Design and modeling is a $350 fee, the first concept mockup is free, and there is no minimum order.',
    link: { to: '/services/custom-trophy/', label: 'Custom trophy pricing' },
  },
  {
    title: 'A sculpture or product from a sketch',
    price: '3D modeling from $250, then printing',
    detail:
      'Without a file, the model comes first. Printing, assembly and finishing are quoted once the geometry is approved.',
    link: { to: '/services/3d-modeling/', label: '3D modeling service' },
  },
  {
    title: 'Copying a physical object',
    price: '3D scanning from $250, then printing',
    detail:
      'Scanning captures the surface as an STL or OBJ mesh. Cleanup, CAD reconstruction and printing are quoted separately.',
    link: { to: '/services/3d-scanning/', label: '3D scanning service' },
  },
  {
    title: 'A large prop or display piece',
    price: 'quoted after review',
    detail:
      'Single prints can reach 6 feet or more, and larger builds are printed in sections and assembled. Standard large-format jobs take 1–2 weeks; complex builds can take 1–2 months.',
    link: {
      to: '/large-scale-3d-printing-service/',
      label: 'Large-scale 3D printing',
    },
  },
]

const savings = [
  'Hollow the model. Material is usually the largest variable cost, and a hollow shell can cut it sharply.',
  'Send a print-ready file. Repairs and modeling are billed time.',
  'Choose the finish you need. Raw or lightly sanded parts cost far less than painted or plated ones.',
  'Order the quantity you will use in one batch, so setup is shared.',
  'Leave time in the schedule. Rush work competes for machine capacity.',
]

const faqs = [
  {
    question: 'How much does it cost to 3D print something?',
    answer: `At home, once you own a printer, a 100-gram PLA print costs about $${homeExampleCosts.totalLow}–$${homeExampleCosts.totalHigh} in filament and electricity. Through PrintAWorld, 3D printing projects start around $30, painted custom trophies usually cost $250–$750 per piece, and complex finished work can reach several thousand dollars. Material, size, machine time and finishing set the price.`,
  },
  {
    question: 'How much does 3D printing cost per hour?',
    answer: `At home, owner measurements put filament printers at roughly 100–200 W while printing, about ${centsFor(100)}–${centsFor(200)}¢ of electricity per print-hour at the ${electricity.month} US average of ${electricity.centsPerKwh}¢ per kWh. Filament costs more than the power. A professional quote combines machine time with material, labor and finishing rather than charging by the hour alone.`,
  },
  {
    question: 'How much does 3D printing cost per gram?',
    answer: `For home printing, PLA filament costs $${homeExample.plaPerKg.low}–$${homeExample.plaPerKg.high} per kilogram on brand stores, about 1–2.4¢ per gram. Professional prices per gram are higher because they include file preparation, cleanup, finishing and the risk of a failed print.`,
  },
  {
    question: 'Why do 3D printing services quote such different prices?',
    answer:
      'Services differ in process, material, whether supports are removed and the part is finished, whether a person reviews the file, and their order minimums and shipping. Compare what each quote includes, not only the total.',
  },
  {
    question: 'What should I send to get an accurate 3D printing quote?',
    answer:
      'Send the 3D file (STL, OBJ, STP, STEP or 3DM) or reference images, plus dimensions, quantity, intended use, material and finish needs, delivery ZIP code and deadline. The written quote confirms scope, price, minimum and schedule.',
  },
  {
    question: 'Can I get something 3D printed the same day?',
    answer:
      'Same-day printing is the exception, not the standard. Print time is only part of the job: preparation, cleanup, curing and finishing take longer, and the quote confirms the schedule.',
  },
]

module.exports = {
  printCostGuide,
  electricity,
  consumables,
  ownerSources,
  homeExample,
  homeExampleCosts,
  routes,
  quoteDrivers,
  examples,
  savings,
  faqs,
}
